import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Divider,
  CircularProgress,
  Alert,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';

import { usePosProfile } from './PosProfileContext';
import useSession from '../auth/useSession';
import {
  getClosingInvoices,
  getOpeningEntry,
  createClosingEntry,
} from '../api/posProfile';

/**
 * Dialog to close the current POS shift.
 *
 * Flow:
 * 1. Fetches the opening entry details + invoices for this shift.
 * 2. Shows summary: invoice count, grand total, payment breakdown.
 * 3. User enters actual closing amounts per payment method.
 * 4. On confirm: creates + submits POS Closing Entry.
 * 5. Calls onSuccess() to clear session and redirect.
 */
export default function CloseShiftDialog({ open, onClose, onSuccess }) {
  const { profile, openingEntry: openingEntryName, cashMovements, cashMovementNet } = usePosProfile();
  const { user } = useSession();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [openingEntry, setOpeningEntry] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [closingAmounts, setClosingAmounts] = useState({});

  // Fetch data when dialog opens
  useEffect(() => {
    if (!open || !openingEntryName) return;

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        // Fetch opening entry details
        const entry = await getOpeningEntry(openingEntryName);
        if (cancelled) return;
        setOpeningEntry(entry);

        // Fetch invoices for this shift
        const invs = await getClosingInvoices({
          start: entry.period_start_date,
          end: new Date().toISOString().replace('T', ' ').split('.')[0],
          posProfile: entry.pos_profile,
          user: entry.user || user,
        });
        if (cancelled) return;
        setInvoices(invs);

        // Initialize closing amounts from opening balances
        const amounts = {};
        (entry.balance_details || []).forEach((d) => {
          amounts[d.mode_of_payment] = '';
        });
        setClosingAmounts(amounts);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load shift data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [open, openingEntryName, user]);

  // Aggregate expected payments from invoices + cash movements
  const paymentSummary = useMemo(() => {
    const summary = {};
    invoices.forEach((inv) => {
      (inv.payments || []).forEach((p) => {
        if (!summary[p.mode_of_payment]) {
          summary[p.mode_of_payment] = { expected: 0, opening: 0, cashAdj: 0 };
        }
        summary[p.mode_of_payment].expected += p.amount || 0;
      });
    });

    // Add opening amounts
    if (openingEntry?.balance_details) {
      openingEntry.balance_details.forEach((d) => {
        if (!summary[d.mode_of_payment]) {
          summary[d.mode_of_payment] = { expected: 0, opening: 0, cashAdj: 0 };
        }
        summary[d.mode_of_payment].opening = d.opening_amount || 0;
      });
    }

    // Apply cash movements to "Cash" mode
    if (cashMovementNet !== 0) {
      if (!summary.Cash) {
        summary.Cash = { expected: 0, opening: 0, cashAdj: 0 };
      }
      summary.Cash.cashAdj = cashMovementNet;
    }

    return summary;
  }, [invoices, openingEntry, cashMovementNet]);

  const grandTotal = invoices
    .filter((i) => !i.is_return)
    .reduce((s, i) => s + (i.grand_total || 0), 0);

  const handleClosingAmountChange = (method, value) => {
    setClosingAmounts((prev) => ({ ...prev, [method]: value }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const paymentReconciliation = Object.entries(paymentSummary).map(
        ([mode_of_payment, { expected, opening, cashAdj }]) => {
          const expectedTotal = expected + opening + (cashAdj || 0);
          return {
            mode_of_payment,
            opening_amount: opening,
            expected_amount: expectedTotal,
            closing_amount: parseFloat(closingAmounts[mode_of_payment]) || 0,
            difference: (parseFloat(closingAmounts[mode_of_payment]) || 0) - expectedTotal,
          };
        },
      );

      await createClosingEntry({
        openingEntry,
        invoices,
        paymentReconciliation,
      });

      onSuccess();
    } catch (err) {
      const msg = err?.response?.data?._server_messages || err.message || 'Failed to close shift';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={!submitting ? onClose : undefined} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PointOfSaleIcon />
          <span>Close Shift</span>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
            <CircularProgress />
            <Typography color="text.secondary">Loading shift data...</Typography>
          </Stack>
        ) : (
          <>
            {/* Shift summary */}
            <Stack spacing={1} sx={{ mb: 2 }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Profile</Typography>
                <Typography variant="body2" fontWeight={600}>{profile?.name}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Opening Entry</Typography>
                <Typography variant="body2">{openingEntryName}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Invoices</Typography>
                <Typography variant="body2" fontWeight={600}>{invoices.length}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="subtitle1" fontWeight={700}>Grand Total</Typography>
                <Typography variant="subtitle1" fontWeight={700}>
                  {grandTotal.toFixed(2)}
                </Typography>
              </Stack>
            </Stack>

            {/* Cash movements summary */}
            {cashMovements.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Cash Movements ({cashMovements.length})
                </Typography>
                <Stack spacing={0.5} sx={{ mb: 1 }}>
                  {cashMovements.map((m) => (
                    <Stack key={m.id} direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={m.type === 'in' ? 'IN' : 'OUT'}
                          size="small"
                          color={m.type === 'in' ? 'success' : 'error'}
                        />
                        <Typography variant="body2">{m.note || '—'}</Typography>
                      </Stack>
                      <Typography variant="body2" fontWeight={600}>
                        {m.type === 'in' ? '+' : '-'}{m.amount.toFixed(2)}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" fontWeight={600}>Net Cash Adjustment</Typography>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color={cashMovementNet >= 0 ? 'success.main' : 'error.main'}
                  >
                    {cashMovementNet >= 0 ? '+' : ''}{cashMovementNet.toFixed(2)}
                  </Typography>
                </Stack>
              </>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Payment reconciliation */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Payment Reconciliation
            </Typography>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Method</TableCell>
                    <TableCell align="right">Opening</TableCell>
                    <TableCell align="right">Expected</TableCell>
                    <TableCell align="right">Closing</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(paymentSummary).map(([method, { expected, opening, cashAdj }]) => (
                    <TableRow key={method}>
                      <TableCell>{method}</TableCell>
                      <TableCell align="right">{opening.toFixed(2)}</TableCell>
                      <TableCell align="right">{(expected + opening + (cashAdj || 0)).toFixed(2)}</TableCell>
                      <TableCell align="right">
                        <TextField
                          size="small"
                          type="number"
                          value={closingAmounts[method] ?? ''}
                          onChange={(e) => handleClosingAmountChange(method, e.target.value)}
                          inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }}
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleSubmit}
          disabled={loading || submitting}
        >
          {submitting ? <CircularProgress size={22} color="inherit" /> : 'Close Shift'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
